'use client';

import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useOrganization, useOrganizationList } from '@clerk/nextjs';
import {
  ChevronDownIcon,
  CheckIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useRouter } from 'next/navigation';
// Admin website-first creation modal — same one as /admin/organizations;
// operator-gated backend, so the trigger below gates on isOperator.
import CreateOrganizationModal from '@/app/admin/organizations/CreateOrganizationModal';
import WebsiteFavicon from './WebsiteFavicon';
import useUserPermissions from '@/hooks/useUserPermissions';
import useOrgActiveStatus from '@/hooks/useOrgActiveStatus';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';

interface OrgSwitcherProps {
  collapsed: boolean;
  planName?: string;
  isSuspended?: boolean;
}

// Visibility/sort tier for an org: active first (0), suspended (1), inactive
// last (2). Unknown/loading status counts as active so orgs don't flicker out
// of the default list while their status resolves.
function orgTier(status?: { isActive?: boolean; isSuspended?: boolean }): number {
  if (status?.isActive === false) return 2;
  if (status?.isSuspended === true) return 1;
  return 0;
}

export default function OrgSwitcher({ collapsed, planName, isSuspended }: OrgSwitcherProps) {
  const t = useTranslations('account.orgSwitcher');
  const { organization: activeOrg } = useOrganization();
  const { userMemberships, isLoaded: orgsLoaded, setActive } = useOrganizationList({
    userMemberships: {
      infinite: true,
      pageSize: 10,
    },
  });
  const { isAdmin, isOperator } = useUserPermissions();
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [operatorSearch, setOperatorSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const observerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Impersonation state — used so the switcher shows the org the operator is
  // currently viewing. Switching to one of the operator's own orgs clears it.
  const impersonationState = useQuery(api.admin.impersonation.getImpersonationState);
  const startImpersonation = useMutation(api.admin.impersonation.startImpersonation);
  const stopImpersonation = useMutation(api.admin.impersonation.stopImpersonation);

  // Debounce the search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(operatorSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [operatorSearch]);

  // Operator org search — only fires for operators with a non-empty search term.
  // Returns ALL orgs matching the search (member and non-member alike).
  const operatorOrgResults = useQuery(
    api.admin.organizations.searchOrgsForPicker,
    isOperator && debouncedSearch.length > 0
      ? { search: debouncedSearch }
      : 'skip',
  );

  const membershipClerkIds = useMemo(
    () => (userMemberships.data ?? []).map((m) => m.organization.id),
    [userMemberships.data],
  );
  const orgActiveStatus = useOrgActiveStatus(membershipClerkIds);
  const mainBrands = useQuery(
    api.organizations.getMainBrandWebsitesByClerkIds,
    membershipClerkIds.length > 0 ? { clerkOrgIds: membershipClerkIds } : 'skip',
  );

  // When impersonating, show the impersonated org name instead of the Clerk-derived one
  const displayName = impersonationState
    ? impersonationState.org.name
    : (activeOrg?.name ?? t('personalAccount'));
  const activeBrandWebsite = activeOrg?.id ? mainBrands?.[activeOrg.id]?.website ?? undefined : undefined;

  useEffect(() => {
    if (!userMemberships.hasNextPage || userMemberships.isFetching) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && userMemberships.fetchNext) {
          userMemberships.fetchNext();
        }
      },
      { threshold: 0.1 }
    );

    const currentObserverRef = observerRef.current;
    if (currentObserverRef) {
      observer.observe(currentObserverRef);
    }

    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
    };
  }, [userMemberships.hasNextPage, userMemberships.isFetching, userMemberships.fetchNext, isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Measure available space and decide whether to open upward.
  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownEstimate = 540;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenUpward(spaceBelow < dropdownEstimate && spaceAbove > spaceBelow);
  }, [isOpen]);

  // Reset search when the dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setOperatorSearch('');
      setDebouncedSearch('');
    }
  }, [isOpen]);

  const handleSwitchOrg = async (orgId: string | null) => {
    if (!setActive) return;
    if (orgId && orgActiveStatus[orgId]?.isActive === false) return;
    // If an operator was impersonating, clear it first and route to /dashboard —
    // the current page may reference the impersonated org's data.
    if (impersonationState) {
      await stopImpersonation({});
      await setActive({ organization: orgId });
      setIsOpen(false);
      router.push('/dashboard');
      return;
    }
    await setActive({ organization: orgId });
    setIsOpen(false);
  };

  const handleImpersonate = async (organizationId: Id<'organizations'>) => {
    await startImpersonation({ organizationId });
    setIsOpen(false);
    router.push('/dashboard');
  };

  const handleExitImpersonation = async () => {
    await stopImpersonation({});
    setIsOpen(false);
    router.push('/dashboard');
  };

  const hasMultipleOrgs = orgsLoaded && userMemberships.data && userMemberships.data.length > 1;
  // Always allow opening the menu while impersonating so the operator has an
  // exit path even if they don't otherwise qualify to switch.
  const canSwitch = hasMultipleOrgs || isAdmin || !!impersonationState;

  // Whether to show the search bar at all. Operators always get it; non-operators
  // get it once they have more than 4 orgs. Critically, also show it whenever any
  // membership is hidden from the default (active-only) list — otherwise a member
  // with ≤4 orgs and a suspended/inactive one would have no way to reach it (e.g.
  // to switch into a suspended org and pay).
  const hasHiddenMemberships = (userMemberships.data ?? []).some(
    (m) => orgTier(orgActiveStatus[m.organization.id]) !== 0,
  );
  const showSearch =
    isOperator || (userMemberships.data ?? []).length > 4 || hasHiddenMemberships;

  // Default (non-search) list: only fully-active orgs. Suspended and inactive
  // memberships are hidden here — they surface only via search. Order preserved
  // from Clerk (all entries are the same active tier).
  const visibleMemberships = useMemo(() => {
    return (userMemberships.data ?? []).filter(
      (m) => orgTier(orgActiveStatus[m.organization.id]) === 0,
    );
  }, [userMemberships.data, orgActiveStatus]);

  // Non-operator client-side filtered memberships when there is a search term.
  // Search shows ALL matches (active, suspended, inactive) sorted by tier, then
  // name.
  const filteredMemberships = useMemo(() => {
    if (!debouncedSearch || isOperator) return null;
    const lower = debouncedSearch.toLowerCase();
    return (userMemberships.data ?? [])
      .filter((m) => m.organization.name.toLowerCase().includes(lower))
      .sort((a, b) => {
        const ta = orgTier(orgActiveStatus[a.organization.id]);
        const tb = orgTier(orgActiveStatus[b.organization.id]);
        if (ta !== tb) return ta - tb;
        return a.organization.name.localeCompare(b.organization.name);
      });
  }, [debouncedSearch, isOperator, userMemberships.data, orgActiveStatus]);

  // Operator search results, sorted by tier (active → suspended → inactive) then name.
  const sortedOperatorResults = useMemo(() => {
    if (!operatorOrgResults) return operatorOrgResults;
    return [...operatorOrgResults].sort((a, b) => {
      const ta = orgTier(a);
      const tb = orgTier(b);
      if (ta !== tb) return ta - tb;
      return a.name.localeCompare(b.name);
    });
  }, [operatorOrgResults]);

  // True when we should show search results instead of the default membership list
  const isSearching = debouncedSearch.length > 0;

  const triggerContent = (
    <>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div className="flex-shrink-0 pl-0.5">
          <WebsiteFavicon
            url={impersonationState ? (impersonationState.org.domain ?? undefined) : activeBrandWebsite}
            fallbackText={displayName}
            size="small"
          />
        </div>
        {!collapsed && (
          <>
            <span className="truncate text-sm" style={{ color: 'var(--color-text-primary)' }}>
              {displayName}
            </span>
            {isSuspended ? (
              <span
                className="text-[10px] border rounded-full px-2 py-0.5 font-semibold flex-shrink-0"
                style={{
                  color: 'var(--color-pink)',
                  borderColor: 'var(--color-pink)',
                  background: 'var(--color-pink-muted)',
                }}
              >
                {t('suspended')}
              </span>
            ) : planName ? (
              <span
                className="text-[10px] border rounded-full px-2 py-0.5 font-semibold flex-shrink-0"
                style={{
                  color: 'var(--color-text-muted)',
                  borderColor: 'var(--color-border)',
                }}
              >
                {planName}
              </span>
            ) : null}
          </>
        )}
      </div>
      {!collapsed && impersonationState && (
        <span
          title={t('impersonating')}
          aria-label={t('impersonating')}
          className="flex-shrink-0 inline-flex items-center"
        >
          <EyeIcon className="w-4 h-4" style={{ color: 'var(--color-purple)' }} />
        </span>
      )}
      {!collapsed && canSwitch && (
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--color-text-muted)' }}
        />
      )}
    </>
  );

  return (
    <div className="relative" ref={menuRef}>
      {canSwitch ? (
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center w-full p-2 rounded-lg transition-all hover:bg-[var(--color-surface-hover)] ${
            collapsed ? 'justify-center' : 'justify-between'
          }`}
          style={impersonationState ? { boxShadow: 'inset 0 0 0 1px var(--color-purple)', background: 'var(--color-purple-muted)' } : undefined}
        >
          {triggerContent}
        </button>
      ) : (
        <div
          className={`flex items-center w-full p-2 rounded-lg ${collapsed ? 'justify-center' : 'justify-between'}`}
          style={impersonationState ? { boxShadow: 'inset 0 0 0 1px var(--color-purple)', background: 'var(--color-purple-muted)' } : undefined}
        >
          {triggerContent}
        </div>
      )}

      {isOpen && canSwitch && (
        <div
          className={`absolute bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-xl overflow-hidden z-50 min-w-[240px] ${
            collapsed
              ? 'left-full ml-2 top-0'
              : openUpward
                ? 'bottom-full mb-2 left-0 w-full'
                : 'top-full mt-2 left-0 w-full'
          }`}
          style={{
            animation: 'orgSwitcherScaleIn 150ms cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <div className="p-1">
            {/* Exit impersonation row — always at the very top when active */}
            {impersonationState && (
              <>
                <button
                  onClick={handleExitImpersonation}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ color: 'var(--color-purple)', background: 'var(--color-purple-muted)' }}
                >
                  <ArrowRightStartOnRectangleIcon className="w-4 h-4 flex-shrink-0" />
                  {t('exitImpersonation')}
                </button>
                <div className="h-px mx-2 my-1" style={{ background: 'var(--color-border)' }} />
              </>
            )}

            {/* Search bar — always shown for operators; shown for non-operators only when they have more than 4 memberships */}
            {showSearch && (
              <div className="px-2 pt-1 pb-1">
                <div
                  className="flex items-center gap-2 rounded-lg px-2"
                  style={{
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface-hover)',
                  }}
                >
                  <MagnifyingGlassIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                  <input
                    type="text"
                    placeholder={isOperator ? t('searchAllOrgs') : t('searchOrganizations')}
                    value={operatorSearch}
                    onChange={(e) => setOperatorSearch(e.target.value)}
                    className="w-full py-1.5 text-xs bg-transparent outline-none"
                    style={{ color: 'var(--color-text-primary)' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}

            <div className="px-2 pb-1">
              {!isSearching ? (
                /* Default view: list of orgs the user is a member of */
                <>
                  <p className="text-[10px] uppercase tracking-wider font-bold px-1 mb-1 pt-1" style={{ color: 'var(--color-text-muted)' }}>
                    {t('yourOrganizations')}
                  </p>
                  <div className="space-y-0.5 max-h-[300px] overflow-y-auto pr-1 scrollbar-hide">
                    {visibleMemberships.length === 0 && !userMemberships.isFetching && (
                      <p className="px-2 py-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        {t('noOrganizationsFound')}
                      </p>
                    )}
                    {visibleMemberships.map((membership) => {
                      const isOrgInactive = orgActiveStatus[membership.organization.id]?.isActive === false;
                      const isCurrent = !impersonationState && activeOrg?.id === membership.organization.id;
                      return (
                        <button
                          key={membership.organization.id}
                          onClick={() => handleSwitchOrg(membership.organization.id)}
                          disabled={isOrgInactive}
                          aria-disabled={isOrgInactive}
                          className={`w-full flex items-center justify-between px-2 py-2 rounded-lg text-sm transition-colors ${
                            isOrgInactive
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-[var(--color-surface-hover)]'
                          }`}
                          style={{ color: 'var(--color-text-primary)' }}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="flex-shrink-0">
                              <WebsiteFavicon
                                url={mainBrands?.[membership.organization.id]?.website ?? undefined}
                                fallbackText={membership.organization.name}
                                size="small"
                              />
                            </div>
                            <span className="truncate">{membership.organization.name}</span>
                            {isOrgInactive && (
                              <span
                                className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                                style={{
                                  background: 'var(--color-surface-hover)',
                                  color: 'var(--color-text-muted)',
                                }}
                              >
                                {t('inactive')}
                              </span>
                            )}
                          </div>
                          {isCurrent && !isOrgInactive && (
                            <CheckIcon className="w-4 h-4 text-lime-500 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}

                    {/* Infinite-scroll sentinel — only needed in the default (non-search) view */}
                    <div ref={observerRef} className="h-1" />

                    {userMemberships.isFetching && (
                      <div className="flex justify-center p-2">
                        <div className="w-4 h-4 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* Search results view */
                <>
                  <p className="text-[10px] uppercase tracking-wider font-bold px-1 mb-1 pt-1" style={{ color: 'var(--color-text-muted)' }}>
                    {t('searchResults')}
                  </p>
                  <div className="space-y-0.5 max-h-[300px] overflow-y-auto pr-1 scrollbar-hide">
                    {isOperator ? (
                      /* Operators: server-side search across all orgs */
                      sortedOperatorResults === undefined ? (
                        <div className="flex justify-center p-2">
                          <div className="w-4 h-4 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : sortedOperatorResults.length === 0 ? (
                        <p className="px-2 py-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                          {t('noOrgsFound')}
                        </p>
                      ) : (
                        sortedOperatorResults.map((result) => {
                          const isMember = !!result.clerkOrgId && membershipClerkIds.includes(result.clerkOrgId);
                          const isCurrentOrg = !impersonationState && !!result.clerkOrgId && activeOrg?.id === result.clerkOrgId;
                          // For member switch targets, disable inactive orgs (mirror existing behavior).
                          // For impersonation targets, keep them clickable even if inactive (prior behavior).
                          const isDisabled = isMember && !result.isActive;
                          return (
                            <button
                              key={result._id}
                              onClick={() =>
                                isMember
                                  ? handleSwitchOrg(result.clerkOrgId!)
                                  : handleImpersonate(result._id)
                              }
                              disabled={isDisabled}
                              aria-disabled={isDisabled}
                              className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm transition-colors ${
                                isDisabled
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'hover:bg-[var(--color-surface-hover)]'
                              }`}
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              <div className="flex-shrink-0">
                                <WebsiteFavicon
                                  url={result.domain ?? undefined}
                                  fallbackText={result.name}
                                  size="small"
                                />
                              </div>
                              <div className="flex-1 text-left min-w-0">
                                <span className="truncate text-xs block">{result.name}</span>
                                {result.domain && (
                                  <span className="truncate text-[10px] block" style={{ color: 'var(--color-text-muted)' }}>
                                    {result.domain}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {!result.isActive && (
                                  <span
                                    className="text-[10px] border rounded-full px-1.5 py-0.5 font-semibold"
                                    style={{
                                      background: 'var(--color-surface-hover)',
                                      color: 'var(--color-text-muted)',
                                      borderColor: 'var(--color-border)',
                                    }}
                                  >
                                    {t('inactive')}
                                  </span>
                                )}
                                {result.isActive && result.isSuspended && (
                                  <span
                                    className="text-[10px] border rounded-full px-1.5 py-0.5 font-semibold"
                                    style={{
                                      color: 'var(--color-pink)',
                                      borderColor: 'var(--color-pink)',
                                      background: 'var(--color-pink-muted)',
                                    }}
                                  >
                                    {t('suspended')}
                                  </span>
                                )}
                                {isMember ? (
                                  <span
                                    className="text-[10px] border rounded-full px-1.5 py-0.5 font-semibold"
                                    style={{
                                      color: 'var(--color-text-muted)',
                                      borderColor: 'var(--color-border)',
                                    }}
                                  >
                                    {t('member')}
                                  </span>
                                ) : (
                                  <span
                                    className="text-[10px] border rounded-full px-1.5 py-0.5 font-semibold"
                                    style={{
                                      color: 'var(--color-purple)',
                                      borderColor: 'var(--color-purple)',
                                      background: 'var(--color-purple-muted)',
                                    }}
                                  >
                                    {t('notAMember')}
                                  </span>
                                )}
                                {isCurrentOrg && (
                                  <CheckIcon className="w-4 h-4 text-lime-500" />
                                )}
                              </div>
                            </button>
                          );
                        })
                      )
                    ) : (
                      /* Non-operators: client-side filter of own memberships */
                      filteredMemberships === null || filteredMemberships.length === 0 ? (
                        <p className="px-2 py-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                          {t('noOrganizationsFound')}
                        </p>
                      ) : (
                        filteredMemberships.map((membership) => {
                          const orgStatus = orgActiveStatus[membership.organization.id];
                          const isOrgInactive = orgStatus?.isActive === false;
                          const isOrgSuspended = orgStatus?.isActive !== false && orgStatus?.isSuspended === true;
                          const isCurrent = !impersonationState && activeOrg?.id === membership.organization.id;
                          return (
                            <button
                              key={membership.organization.id}
                              onClick={() => handleSwitchOrg(membership.organization.id)}
                              disabled={isOrgInactive}
                              aria-disabled={isOrgInactive}
                              className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm transition-colors ${
                                isOrgInactive
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'hover:bg-[var(--color-surface-hover)]'
                              }`}
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              <div className="flex-shrink-0">
                                <WebsiteFavicon
                                  url={mainBrands?.[membership.organization.id]?.website ?? undefined}
                                  fallbackText={membership.organization.name}
                                  size="small"
                                />
                              </div>
                              <div className="flex-1 text-left min-w-0">
                                <span className="truncate text-xs block">{membership.organization.name}</span>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {isOrgInactive && (
                                  <span
                                    className="text-[10px] border rounded-full px-1.5 py-0.5 font-semibold"
                                    style={{
                                      background: 'var(--color-surface-hover)',
                                      color: 'var(--color-text-muted)',
                                      borderColor: 'var(--color-border)',
                                    }}
                                  >
                                    {t('inactive')}
                                  </span>
                                )}
                                {isOrgSuspended && (
                                  <span
                                    className="text-[10px] border rounded-full px-1.5 py-0.5 font-semibold"
                                    style={{
                                      color: 'var(--color-pink)',
                                      borderColor: 'var(--color-pink)',
                                      background: 'var(--color-pink-muted)',
                                    }}
                                  >
                                    {t('suspended')}
                                  </span>
                                )}
                                {isCurrent && !isOrgInactive && (
                                  <CheckIcon className="w-4 h-4 text-lime-500" />
                                )}
                              </div>
                            </button>
                          );
                        })
                      )
                    )}
                  </div>
                </>
              )}
            </div>

            {isOperator && (
              <>
                <div className="h-px mx-2 my-1" style={{ background: 'var(--color-border)' }} />
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setShowCreateOrgModal(true);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-[var(--color-surface-hover)]"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <PlusIcon className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                  {t('createOrganization')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <CreateOrganizationModal
        isOpen={showCreateOrgModal}
        onClose={() => setShowCreateOrgModal(false)}
      />

      <style jsx>{`
        @keyframes orgSwitcherScaleIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-4px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
